import { c as _c } from "react/compiler-runtime";
import SvgAdd from '@common/__generated__/icon/SvgAdd';
import SvgCheck from '@common/__generated__/icon/SvgCheck';
import SvgDelete from '@common/__generated__/icon/SvgDelete';
import { Button, Spinner } from '@common/components';
import { S3_CDN_TYPE, type S3_PATH_TYPE } from '@common/constants/s3';
import { useToast } from '@common/hooks';
import { cn } from '@core/utils';
import { useMutation } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { AuthApi } from '@/api';
import { useFileUpload } from '@/hooks/useFileUpload';
import { Image as S3Image } from './Image';
interface PlusBoxProps {
  className?: string;
  onUpload?: (fileUrl?: string) => void;
  onDelete?: () => void;
  limitByte?: number;
  limitPixel?: {
    width: number;
    height: number;
  };
  type: `${S3_PATH_TYPE}`;
  imageUrl?: string;
  accepts?: string;
  s3CdnType?: `${S3_CDN_TYPE}`;
  disabled?: boolean;
  previewBlank?: boolean;
  size?: number;
  /** 지정하면 정사각 플러스 박스 대신 아이콘+라벨 트리거로 렌더한다(크기는 className이 결정) */
  label?: ReactNode;
}
export const FileUploadPlusBox = t0 => {
  const $ = _c(57);
  const {
    className,
    onUpload,
    onDelete,
    limitByte,
    limitPixel,
    type,
    imageUrl,
    accepts: t1,
    s3CdnType: t2,
    disabled,
    previewBlank,
    size: t3,
    label
  } = t0;
  const accepts = t1 === undefined ? "image/*" : t1;
  const s3CdnType = t2 === undefined ? "S3_CDN_TYPE_WELFAREHELLO" : t2;
  const size = t3 === undefined ? 72 : t3;
  const {
    toast
  } = useToast();
  let t4;
  if ($[0] !== accepts) {
    t4 = {
      accept: accepts
    };
    $[0] = accepts;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const {
    getFile
  } = useFileUpload(t4);
  let t5;
  if ($[2] !== type) {
    t5 = {
      mutationFn: file => AuthApi["post:/wello/v2/auth/s3"]({
        body: {
          fileUrlList: [file],
          s3CdnTypeCd: "S3_CDN_TYPE_WELFAREHELLO",
          type
        }
      })
    };
    $[2] = type;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const {
    mutateAsync: uploadFileAsyncMutate,
    isPending: isUploading
  } = useMutation(t5);
  const isThumbnailMode = !!imageUrl;
  const isUploadDisabled = isUploading || isThumbnailMode || disabled;
  const isLabelMode = !!label && !imageUrl;
  let t6;
  bb0: {
    if (isUploading) {
      let t7;
      if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
        t7 = <Spinner size={14} />;
        $[4] = t7;
      } else {
        t7 = $[4];
      }
      t6 = t7;
      break bb0;
    }
    if (isLabelMode) {
      let t7;
      if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
        t7 = <SvgAdd className="size-16 text-Gray-900" />;
        $[5] = t7;
      } else {
        t7 = $[5];
      }
      let t8;
      if ($[6] !== label) {
        t8 = <>{t7}<span className="font-700 text-Gray-900 typo-body4">{label}</span></>;
        $[6] = label;
        $[7] = t8;
      } else {
        t8 = $[7];
      }
      t6 = t8;
      break bb0;
    }
    if (!imageUrl) {
      let t7;
      if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
        t7 = <SvgAdd className="size-20 text-Gray-400" />;
        $[8] = t7;
      } else {
        t7 = $[8];
      }
      t6 = t7;
      break bb0;
    }
    switch (s3CdnType) {
      case S3_CDN_TYPE.WELFARE_HELLO:
        {
          const t7 = previewBlank ? imageUrl : undefined;
          let t8;
          if ($[9] !== previewBlank) {
            t8 = e => {
              if (!previewBlank) {
                e.preventDefault();
              }
            };
            $[9] = previewBlank;
            $[10] = t8;
          } else {
            t8 = $[10];
          }
          let t9;
          if ($[11] !== imageUrl || $[12] !== size) {
            t9 = <S3Image src={imageUrl} alt={"\uC378\uB124\uC77C \uC774\uBBF8\uC9C0"} width={size} height={size} draggable={false} className="size-full select-none rounded-small object-cover object-center" />;
            $[11] = imageUrl;
            $[12] = size;
            $[13] = t9;
          } else {
            t9 = $[13];
          }
          let t10;
          if ($[14] !== t7 || $[15] !== t8 || $[16] !== t9) {
            t10 = <a href={t7} target="_blank" rel="noopener noreferrer external" className="size-full rounded-small" onClick={t8}>{t9}</a>;
            $[14] = t7;
            $[15] = t8;
            $[16] = t9;
            $[17] = t10;
          } else {
            t10 = $[17];
          }
          t6 = t10;
          break bb0;
        }
      case S3_CDN_TYPE.ENCRYPT:
        {
          let t7;
          if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
            t7 = <SvgCheck className="text-G-200" />;
            $[18] = t7;
          } else {
            t7 = $[18];
          }
          t6 = t7;
          break bb0;
        }
    }
    t6 = undefined;
  }
  const thumbnail = t6;
  const t7 = isLabelMode ? "w-full" : "size-fit";
  let t8;
  if ($[19] !== t7) {
    t8 = cn("relative", t7);
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const t9 = isLabelMode ? "block w-full" : "size-fit";
  let t10;
  if ($[21] !== isUploadDisabled || $[22] !== t9) {
    t10 = cn("cursor-pointer", t9, {
      "cursor-default": isUploadDisabled
    });
    $[21] = isUploadDisabled;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== className || $[25] !== isLabelMode || $[26] !== isUploadDisabled) {
    t11 = cn("relative rounded-small border border-Gray-black/5 bg-Gray-50 transition-colors flex-center hover:bg-Gray-50/70", isLabelMode && ["w-full gap-4 border-transparent", "hover:opacity-80", !isUploadDisabled && "active:opacity-80", isUploadDisabled && "opacity-40"], className);
    $[24] = className;
    $[25] = isLabelMode;
    $[26] = isUploadDisabled;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== isLabelMode || $[29] !== size) {
    t12 = isLabelMode ? undefined : {
      width: size,
      height: size
    };
    $[28] = isLabelMode;
    $[29] = size;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== t11 || $[32] !== t12 || $[33] !== thumbnail) {
    t13 = <article className={t11} style={t12}>{thumbnail}</article>;
    $[31] = t11;
    $[32] = t12;
    $[33] = thumbnail;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== getFile || $[36] !== limitByte || $[37] !== limitPixel || $[38] !== onUpload || $[39] !== toast || $[40] !== uploadFileAsyncMutate) {
    t14 = async () => {
      const file_0 = await getFile();
      if (!file_0) {
        return;
      }
      if (limitByte && file_0.size > limitByte) {
        toast({
          message: `이미지 용량은 ${limitByte / 1024 / 1024}MB 이하여야 합니다.`,
          type: "warning"
        });
        return;
      }
      if (limitPixel) {
        const reader = new FileReader();
        reader.onload = () => {
          const img = new Image();
          img.onload = () => {
            if (img.width > limitPixel.width || img.height > limitPixel.height) {
              toast({
                message: `이미지 크기는 ${limitPixel.width}x${limitPixel.height} 픽셀 이하여야 합니다.`,
                type: "warning"
              });
              return;
            }
          };
        };
      }
      const {
        data
      } = await uploadFileAsyncMutate(file_0);
      if (!data?.context?.contents?.[0].fileUrl) {
        toast({
          message: "\uC774\uBBF8\uC9C0 \uC5C5\uB85C\uB4DC\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4.",
          type: "fail"
        });
        return;
      }
      onUpload?.(data?.context?.contents?.[0].fileUrl);
    };
    $[35] = getFile;
    $[36] = limitByte;
    $[37] = limitPixel;
    $[38] = onUpload;
    $[39] = toast;
    $[40] = uploadFileAsyncMutate;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  let t15;
  if ($[42] !== isUploadDisabled || $[43] !== t14) {
    t15 = <input type="button" className="hidden" disabled={isUploadDisabled} onClick={t14} />;
    $[42] = isUploadDisabled;
    $[43] = t14;
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  let t16;
  if ($[45] !== t10 || $[46] !== t13 || $[47] !== t15) {
    t16 = <label className={t10}>{t13}{t15}</label>;
    $[45] = t10;
    $[46] = t13;
    $[47] = t15;
    $[48] = t16;
  } else {
    t16 = $[48];
  }
  let t17;
  if ($[49] !== disabled || $[50] !== isThumbnailMode || $[51] !== onDelete) {
    t17 = isThumbnailMode && !disabled && <Button onClick={e_0 => {
      e_0.stopPropagation();
      onDelete?.();
    }} theme="sub-text" roundness="capsule" className="absolute right-2 top-2 size-20 -translate-y-1/2 translate-x-1/2 bg-Gray-black p-0"><SvgDelete className="size-10 text-Gray-white" /></Button>;
    $[49] = disabled;
    $[50] = isThumbnailMode;
    $[51] = onDelete;
    $[52] = t17;
  } else {
    t17 = $[52];
  }
  let t18;
  if ($[53] !== t16 || $[54] !== t17 || $[55] !== t8) {
    t18 = <article className={t8}>{t16}{t17}</article>;
    $[53] = t16;
    $[54] = t17;
    $[55] = t8;
    $[56] = t18;
  } else {
    t18 = $[56];
  }
  return t18;
};